import { useState, useCallback } from 'react';

/**
 * frontend/src/components/DateWarnings.jsx
 *
 * The API validates dates in two tiers. A reversed pair (end before start) is
 * rejected outright with a 400 the form already shows. A child falling outside
 * its parent's window is *allowed* — too many existing records break that rule
 * to block on it — and comes back as `date_warnings` on the saved row.
 *
 * Until now nothing rendered them, so the record saved and the warning was
 * thrown away. This shows them after a save, without interrupting it.
 *
 * Usage:
 *   const warn = useDateWarnings();
 *   ...
 *   const r = await tasksAPI.update(id, form);
 *   warn.show(r.data);            // reads r.data.date_warnings
 *   ...
 *   <DateWarnings {...warn} />    // near the top of the page's return
 */

export function useDateWarnings() {
  const [warnings, setWarnings] = useState([]);

  const show = useCallback((saved) => {
    const list = saved?.date_warnings;
    setWarnings(Array.isArray(list) ? list : []);
  }, []);

  const dismiss = useCallback(() => setWarnings([]), []);

  return { warnings, show, dismiss };
}

export default function DateWarnings({ warnings, dismiss, label = 'Saved' }) {
  if (!warnings?.length) return null;
  return (
    <div className="p-3 rounded-xl text-sm mb-4 flex items-start gap-3"
      style={{ background: 'rgba(217,119,6,.10)', border: '1px solid rgba(217,119,6,.28)' }}>
      <span className="font-semibold flex-shrink-0" style={{ color: '#d97706' }}>{label},</span>
      <div className="flex-1 min-w-0">
        <p className="mb-1" style={{ color: '#d97706' }}>
          but the dates sit outside the parent&rsquo;s window:
        </p>
        <ul className="list-disc pl-5 space-y-0.5" style={{ color: 'var(--text)' }}>
          {warnings.map((w, i) => <li key={i}>{w}</li>)}
        </ul>
      </div>
      <button onClick={dismiss} className="text-xs underline flex-shrink-0"
        style={{ color: '#d97706' }}>Dismiss</button>
    </div>
  );
}
